import {endpoint} from '~/api';
import {tagListData} from '~/components/Controls/Tags';
import {ID} from '~/lib/model';
import {IPaginator} from '~/lib/paginator';
import {IBidder} from '~/modules/Bidders/models/Bidder';
import {adaptBidderSummary, IBidderSummary} from '~/modules/Bidders/models/BidderSummary';
import {IBidderGroup, IBulkPauseResult} from '~/modules/Bidders/models/BidderGroup';
import {adaptBidderLite, IBidderLite} from '~/modules/Bidders/models/BidderLite';
import {IBidderDaySpendLimits} from '~/modules/Bidders/models/BidderSpendLimit';
import {IPlacementFormatsProposal} from '~/modules/Bidders/models/PlacementFormatsProposal';
import {IBidderSpendRow} from '~/modules/Bidders/models/BidderSpendRow';
import {IPreTargetingConfig, IPreTargetingVariable} from '~/modules/Bidders/models/PreTargeting';
import {IRule} from '~/modules/Bidders/models/Rule';
import {SchemaInfo} from '~/modules/Bidders/models/Schema';
import {ISource, ISourcePreview} from '~/modules/Sources/models/Source';
import { IAgency } from '../Clients/models/Agency';

export const fetchBidder = (bidderId: ID) => endpoint.get<IBidder>(`/bidder/${bidderId}`).then(r => r.data);

export const fetchPlacementFormatsProposal = (bidderId: ID) =>
    endpoint.get<IPlacementFormatsProposal>(`/bidder/${bidderId}/placement-formats/proposal`).then(r => r.data);

export const createBidder = (bidder: IBidder) => endpoint.post<IBidder>(`/agency/${bidder.agencyId}/bidder`, {
    ...bidder,
    id: undefined
}).then(r => r.data);

export const updateBidder = (bidder: IBidder) => endpoint.post<IBidder>(`/bidder/${bidder.id}`, {
    ...bidder,
    id: undefined
}).then(r => r.data);

export const copyBidder = (id: ID) => endpoint.post<IBidder>(`/bidder/${id}/copy`).then(r => r.data);

export const copyAgency = (id: ID, body?: Record<string, unknown>) =>
    endpoint.post<IAgency>(`/agency/${id}/copy`, body).then(r => r.data);

export const updateBidders = (bidders: IBidder[] | IBidderSummary[] | IBidder) =>
    endpoint.patch('/bidder', bidders).then(r => r.data);

export const deleteBidder = (bidder: IBidder) => endpoint.delete(`/bidder/${bidder.id}`).then(r => r.data);

export type IBiddersFilter = {
    active?: boolean | null;
    bidderSearch?: string;
    agencySearch?: string;
    auctionCategory?: 'performance' | 'branded' | null;
    tag?: string;
    pageNo?: number | null;
    pageSize?: number | null;
    bulkActions?: boolean;
    checkAll?: boolean;
    refreshTags?: boolean;
};

export interface IBiddersPaginator {
    pageNo?: number;
    pageSize?: number;
}

interface IFetchBiddersPageOptions {
    signal?: AbortSignal;
}

export const prepareBidderSubmitData = (record) => ({
    ...record,
    minBidFloor: record.minBidFloor === '' ? null : record.minBidFloor,
    maxBidFloor: record.maxBidFloor === '' ? null : record.maxBidFloor,
    impExpirationTime: record.impExpirationTime === '' ? null : record.impExpirationTime,
    wseat: tagListData(record.wseat),
    bseat: tagListData(record.bseat)
});

const normalizePositiveNumber = (value: unknown, fallback: number) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
        return Math.floor(parsed);
    }
    return fallback;
};

const normalizeNonNegativeNumber = (value: unknown, fallback: number) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) {
        return Math.floor(parsed);
    }
    return fallback;
};

const adaptBiddersPaginator = (
    responseData: IPaginator<unknown>,
    fallback: IBiddersPaginator
): IPaginator<IBidderSummary> => {
    const fallbackPage = normalizeNonNegativeNumber(fallback.pageNo, 0);
    const fallbackPageSize = normalizePositiveNumber(fallback.pageSize, 25);
    const items = Array.isArray(responseData?.items) ? responseData.items.map(adaptBidderSummary) : [];
    return {
        items,
        count: normalizeNonNegativeNumber(responseData?.count, items.length),
        page: normalizeNonNegativeNumber(responseData?.page, fallbackPage),
        pageSize: normalizePositiveNumber(responseData?.pageSize, fallbackPageSize)
    };
};

export const fetchBiddersPage = (
    filter: IBiddersFilter = {},
    paginator: IBiddersPaginator = {},
    options: IFetchBiddersPageOptions = {}
) => {
    const params = new URLSearchParams();
    const {active, bidderSearch, agencySearch, auctionCategory, tag, pageNo, pageSize} = filter ?? {};
    const resolvedPageNo = pageNo ?? paginator.pageNo;
    const resolvedPageSize = pageSize ?? paginator.pageSize;
    const appendTrimmed = (key: string, value?: string | null) => {
        const trimmed = value?.trim();
        if (trimmed) {
            params.append(key, trimmed);
        }
    };
    if (typeof active === 'boolean') {
        params.append('enabled', String(active));
    }
    appendTrimmed('bidderSearch', bidderSearch);
    appendTrimmed('agencySearch', agencySearch);
    if (auctionCategory) {
        params.append('auctionCategory', auctionCategory);
    }
    appendTrimmed('tag', tag);
    if (typeof resolvedPageNo === 'number' && Number.isFinite(resolvedPageNo)) {
        params.append('pageNo', String(Math.floor(resolvedPageNo)));
    }
    if (typeof resolvedPageSize === 'number' && Number.isFinite(resolvedPageSize) && resolvedPageSize > 0) {
        params.append('pageSize', String(Math.floor(resolvedPageSize)));
    }
    const suffix = params.toString();
    return endpoint.get<IPaginator<unknown>>(
        `/bidder-lite${suffix ? `?${suffix}` : ''}`,
        {signal: options.signal}
    )
        .then(r => adaptBiddersPaginator(r.data, {
            pageNo: resolvedPageNo,
            pageSize: resolvedPageSize
        }));
};

const ALL_BIDDERS_PAGE_SIZE = 100000;

export const fetchBidders = (filter: IBiddersFilter = {}) =>
    fetchBiddersPage(filter, {pageNo: 0, pageSize: ALL_BIDDERS_PAGE_SIZE}).then(result => result.items);

export const fetchBiddersLite = (search?: string): Promise<IBidderLite[]> => {
    const params = new URLSearchParams();
    if (search && search.trim()) {
        params.append('search', search.trim());
    }
    const suffix = params.toString();
    return endpoint.get(`/bidders-lite${suffix ? `?${suffix}` : ''}`)
        .then(r => (Array.isArray(r.data) ? r.data : []))
        .then(items => items.map(adaptBidderLite).filter((item): item is IBidderLite => Boolean(item)));
};

export type IBidderGroupsFilter = {
    id?: ID;
    name?: string;
};

export const fetchBidderProtocols = () => endpoint.get<string[]>('/bidder/protocol').then(r => r.data);

export const fetchBidderStatuses = () => endpoint.get<string[]>('/bidder/status').then(r => r.data);

export const fetchBidderFloorTypes = () => endpoint.get<string[]>('/bidder/floor-type').then(r => r.data);

export const fetchBidderDc = () => endpoint.get<string[]>('/bidder/dc').then(r => r.data);

export const fetchSources = () => endpoint.get<ISource[]>('/sources').then(r => r.data);

export const fetchSourcePreviews = () =>
  endpoint.get<ISourcePreview[]>("/sellers/preview").then((r) => r.data);


export const fetchPreTargetingConfigs = (bidderId: ID) => endpoint.get<IPreTargetingConfig[]>(`/bidder/${bidderId}/pretargeting`)
    .then(r => r.data);

export const fetchPreTargetingConfig = (preTargetingConfigId: ID) =>
    endpoint.get<IPreTargetingConfig>(`/pretargeting/${preTargetingConfigId}`).then(r => r.data);

export const createPreTargetingConfig = (preTargeting: IPreTargetingConfig) => endpoint.post<IPreTargetingConfig>(
    `/bidder/${preTargeting.bidderId}/pretargeting`,
    preTargeting
).then(r => r.data);

export const deletePreTargetingConfig = (preTargeting: IPreTargetingConfig) => endpoint.delete(
    `/pretargeting/${preTargeting.id}`
).then(r => r.data);

export const updatePreTargetingConfig = (preTargeting: IPreTargetingConfig) => endpoint.post<IPreTargetingConfig>(
    `/pretargeting/${preTargeting.id}`,
    preTargeting
).then(r => r.data);

export const fetchPreTargetingRulesText = (preTargetingConfigId: number) => endpoint.get<string>(
    `/pretargeting/${preTargetingConfigId}/rules`,
    {
        headers: {
            'Content-Type': 'text/plain',
            'Accept': 'text/plain',
        },
        responseType: 'text'
    }
).then(r => r.data);

export const updatePreTargetingRulesText = (preTargetingConfigId: number, rules: string) => endpoint.post(
    `/pretargeting/${preTargetingConfigId}/rules`,
    rules,
    {
        headers: {
            'Accept': 'text/plain',
            'Content-Type': 'text/plain'
        },
        responseType: 'text',
    }
).then(r => r.data);

export const fetchPreTargetingVariableValue = (preTargetingId: ID, name: string) => endpoint.get<string>(
    `/pretargeting/${preTargetingId}/vars/${name}`
).then(r => {
    r.data = JSON.stringify(r.data);
    return r;
}).then(r => r.data);

export const fetchPreTargetingVariables = (preTargetingConfigId: ID) => endpoint.get<IPreTargetingVariable[]>(
    `/pretargeting/${preTargetingConfigId}/vars`
).then(r => Promise.all(r.data.map(async variable => ({
    value: await fetchPreTargetingVariableValue(preTargetingConfigId, variable.name),
    name: variable.name,
    preTargetingConfigId
}))));

export const deletePreTargetingVariable = (preTargetingVariable: IPreTargetingVariable) => endpoint.delete(
    `/pretargeting/${preTargetingVariable.preTargetingConfigId}/vars/${preTargetingVariable.name}`
).then(r => r.data);

export const createPreTargetingVariable = (preTargetingVariable: IPreTargetingVariable) => endpoint.post<IPreTargetingVariable>(
    `/pretargeting/${preTargetingVariable.preTargetingConfigId}/vars/${preTargetingVariable.name}`,
    JSON.parse(preTargetingVariable.value)
).then(() => preTargetingVariable);

export const updatePreTargetingVariable = (preTargetingVariable: IPreTargetingVariable) => endpoint.put(
    `/pretargeting/${preTargetingVariable.preTargetingConfigId}/vars/${preTargetingVariable.name}`,
    JSON.parse(preTargetingVariable.value)
).then(() => preTargetingVariable);

export const fetchPreTargetingRules = (preTargetingConfigId: ID) =>
    endpoint.get<IRule[]>(`/pretargeting/${preTargetingConfigId}/rules`).then(r => r.data);

export const updatePreTargetingRules = (preTargetingConfigId: ID, rules: IRule[]) =>
    endpoint.post(`/pretargeting/${preTargetingConfigId}/rules`, rules);

export const deletePreTargetingRules = (preTargetingConfigId: ID) =>
    endpoint.delete(`/pretargeting/${preTargetingConfigId}/rules`).then(r => r.data);

export const fetchPreTargetingSchema = () => endpoint.get<SchemaInfo>('/attribute/info').then(r => r.data);

export const fetchCurrentHourSpend = (bidderId: ID) => endpoint.get<IBidderSpendRow>(`/bidder/${bidderId}/current-spend`).then(r => r.data);

export const fetchBidderSpendLimits = (bidderId: ID, date: string) =>
    endpoint.get<IBidderDaySpendLimits>(`/bidders/${bidderId}/spend-limits`, {params: {date}}).then(r => r.data);

export const replanSpendLimits = (date: string) =>
    endpoint.post<{items: {bidderId: ID; hourly: number[]}[]}>('/bidders/spend-limits', null, {params: {date}}).then(r => r.data);

export const replanBidderSpendLimits = (bidderId: ID, date: string) =>
    endpoint.post<IBidderDaySpendLimits>(`/bidders/${bidderId}/spend-limits`, null, {params: {date}}).then(r => r.data);

export const scaleBidderSpendLimits = (bidderId: ID, factor: number) =>
    endpoint.post<IBidderDaySpendLimits>(`/bidders/${bidderId}/spend-limits/scale`, null, {params: {factor}}).then(r => r.data);

export const fetchAvailableAttributes = () => {
    return endpoint
        .get('/attribute/info')
        .then(response => response.data);
};

type BidderIdPayload = number | { value?: number; id?: number };

const normalizeBidderIds = (bidderIds?: BidderIdPayload[]) => (bidderIds ?? [])
    .map(id => {
        if (typeof id === 'number') {
            return id;
        }
        if (typeof id === 'object' && id !== null) {
            if (typeof id.value === 'number') {
                return id.value;
            }
            if (typeof id.id === 'number') {
                return id.id;
            }
        }
        return undefined;
    })
    .filter((id): id is number => typeof id === 'number');

const extractId = (value: any): number | null => {
    if (typeof value === 'number') {
        return value;
    }
    if (value && typeof value === 'object') {
        if (typeof value.value === 'number') {
            return value.value;
        }
        if (typeof value.id === 'number') {
            return value.id;
        }
    }
    return null;
};

const adaptBidderLiteList = (bidders?: any[]): IBidderLite[] => (Array.isArray(bidders) ? bidders : [])
    .map(adaptBidderLite)
    .filter((bidder): bidder is IBidderLite => Boolean(bidder));

const adaptBidderGroupResponse = (group: any): IBidderGroup => {
    const bidders = adaptBidderLiteList(group?.bidders);
    const bidderIds = bidders.length > 0
        ? bidders.map(bidder => bidder.id)
        : normalizeBidderIds(group?.bidderIds);
    return {
        ...group,
        id: extractId(group?.id),
        name: group?.name ?? '',
        bidders,
        bidderIds,
        ivtSensitive: Boolean(group?.ivtSensitive)
    };
};

const createBidderLitePayload = (bidders: IBidderLite[] = []) => bidders.map(bidder => ({
    id: bidder.id,
    name: bidder.name,
    agencyId: bidder.agencyId,
    enabled: bidder.enabled,
    status: bidder.status
}));

const prepareBidderGroupPayload = (group: IBidderGroup) => ({
    name: group.name,
    bidders: createBidderLitePayload(group.bidders ?? []),
    ivtSensitive: group.ivtSensitive
});

const buildBidderGroupsQuery = (filter: IBidderGroupsFilter = {}) => {
    const params = new URLSearchParams();
    if (filter?.id != null && filter.id !== '') {
        params.append('id', filter.id.toString());
    }
    if (filter?.name && filter.name.trim()) {
        params.append('name', filter.name.trim());
    }
    const query = params.toString();
    return query ? `?${query}` : '';
};

export const fetchBidderGroups = (filter: IBidderGroupsFilter = {}) =>
    endpoint.get<IBidderGroup[]>(`/bidder-groups${buildBidderGroupsQuery(filter)}`).then(r => r.data.map(adaptBidderGroupResponse));

export const fetchBidderGroup = (groupId: ID) => endpoint.get<IBidderGroup>(`/bidder-groups/${groupId}`)
    .then(r => adaptBidderGroupResponse(r.data));

export const createBidderGroup = (group: IBidderGroup) => endpoint.post<IBidderGroup>('/bidder-groups', prepareBidderGroupPayload(group))
    .then(r => adaptBidderGroupResponse(r.data));

export const updateBidderGroup = (group: IBidderGroup) =>
    endpoint.put<IBidderGroup>(`/bidder-groups/${group.id}`, prepareBidderGroupPayload(group))
        .then(r => adaptBidderGroupResponse(r.data));

export const deleteBidderGroup = (group: IBidderGroup) => endpoint.delete(`/bidder-groups/${group.id}`).then(r => r.data);

export const pauseBidderGroupBidders = (groupId: ID) =>
    endpoint.post<IBulkPauseResult>(`/bidder-groups/${groupId}/pause`).then(r => r.data);

export const unpauseBidderGroupBidders = (groupId: ID) =>
    endpoint.post<IBulkPauseResult>(`/bidder-groups/${groupId}/unpause`).then(r => r.data);

export const fetchBidderGroupsByBidder = (bidderId: ID) => endpoint.get<IBidderGroup[]>(`/bidders/${bidderId}/groups`)
    .then(r => r.data.map(adaptBidderGroupResponse));
